import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import { faChevronDown } from '@document-crunch/pro-regular-svg-icons'
import { useMeasure } from '@uidotdev/usehooks'
import { useMemo, useState } from 'react'
import { LocationTree } from '@/app/(dashboard)/upload/step1/LocationTree'
import { useGetFolderTree } from '@/dc-shared-web/src/hooks/api/folder/useGetFolderForMove'
import { FS, FS_CATEGORY } from '@/dc-shared-web/src/lib/trackingConstants'
import { FolderTree } from '@/dc-shared-web/src/hooks/api/folder/types'

export type LocationSelectProps = {
  value: LocationType
  onChange: (value: LocationType) => void
  disabled?: boolean
  teamUuid: string
}

export const LocationSelect = ({
  value,
  onChange,
  disabled = false,
  teamUuid,
}: LocationSelectProps) => {
  const { trackEvent } = useFSTrackEvent()
  const { data: foldersAndProjects = [], isLoading } =
    useGetFolderTree(teamUuid)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [ref, { width }] = useMeasure<HTMLButtonElement>()

  const handleLocationSelect = (location: LocationType) => {
    onChange(location)

    // Flatten all nested locations to include children
    const flattenLocations = (items: FolderTree[]): FolderTree[] => {
      const result: FolderTree[] = []
      items.forEach((item) => {
        result.push(item)
        if (item.children && item.children.length > 0) {
          result.push(...flattenLocations(item.children))
        }
      })
      return result
    }

    const allLocations = [
      //add dashboard since isn't returned by useGetFolderTree
      { name: 'Dashboard', type: 'dashboard', id: 'dashboard' },
      ...flattenLocations(foldersAndProjects),
    ]
    const itemIndex = allLocations.findIndex(
      (item) => item.name === location.name && item.type === location.type
    )

    trackEvent(FS.DROPDOWN_OPTION_SELECTED, {
      name: location.name,
      parent: 'UploadDestination',
      page_category: FS_CATEGORY.UPLOAD,
      item_length: allLocations.length,
      item_order: itemIndex + 1,
    })

    setIsPopoverOpen(false)
  }

  const handleOpenChange = (isOpen: boolean) => {
    setIsPopoverOpen(isOpen)

    if (isOpen) {
      trackEvent(FS.DROPDOWN_CLICKED, {
        name: 'UploadDestination',
        text: 'Destination',
        page_category: FS_CATEGORY.UPLOAD,
      })
    }
  }

  const disableTrigger = useMemo(() => {
    return disabled || isLoading || foldersAndProjects.length === 0
  }, [disabled, isLoading, foldersAndProjects.length])

  return (
    <Popover open={isPopoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant='outline'
          fullWidth
          className='tw-justify-between'
          disabled={disableTrigger}
          data-testid='destination-dropdown'
        >
          <div className='tw-flex-1 tw-font-normal tw-text-left'>
            {value.name}
          </div>
          <Icon icon={faChevronDown} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='tw-p-0 tw-overflow-hidden'
        style={{ width: width ?? 0 }}
      >
        <div className='tw-h-64 tw-overflow-auto tw-text-sm'>
          <LocationTree
            value={value}
            onChange={handleLocationSelect}
            teamUuid={teamUuid}
            foldersAndProjects={foldersAndProjects}
            isLoading={isLoading}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
